import { useQuery } from '@tanstack/react-query';
import settings from 'services/app-settings';
import { useCurrentClientManager } from '@cko/dashboard-shared';
import { useCallback } from 'react';

import {
  useGetPrismLinkAvailabilityFetcher,
  ClientsResponse,
} from './useGetPrismLinkAvailabilityFetcher';

const clientsEndpoint = `${settings.api.prismApiBasePath}/clients`;
type LinkAvailabilityConfig = {
  disableForSubMerchants: boolean;
  premiumAccessOnly: boolean;
};

//Have removed the session store and asked react query to handle caching, we can see if this causes issues when we use this for link filtering.

interface UseGetPrismLinkAvailabilityOptions {
  enabled?: boolean;
  staleTime?: boolean;
}

export const useGetPrismLinkAvailability = (options?: UseGetPrismLinkAvailabilityOptions) => {
  const { enabled } = options || {};
  const { client, entity } = useCurrentClientManager();
  const prismClientsQueryKeys = [clientsEndpoint, client.id];
  const fetcher = useGetPrismLinkAvailabilityFetcher();

  const {
    data: prismClientsData,
    isLoading: isPrismClientsLoading,
    isError: isPrismClientsError,
  } = useQuery<ClientsResponse>(prismClientsQueryKeys, fetcher, {
    enabled: Boolean(client.id) && enabled,
  });

  const getPrismLinkAvailability = useCallback(
    (props: LinkAvailabilityConfig) => {
      const { disableForSubMerchants, premiumAccessOnly } = props;

      if (!prismClientsData?.enabled) {
        return false;
      }

      if (disableForSubMerchants) {
        const subMerchant = prismClientsData?.children_entity_and_sub_merchant_ids.find(
          (ent: { entity_id: string }) => ent.entity_id === entity?.id,
        );

        if (subMerchant) {
          return false;
        }
      }

      if (premiumAccessOnly) {
        if (prismClientsData?.tier !== 'premium') {
          return false;
        }
      }

      return true;
    },
    [prismClientsData, entity],
  );

  const isLoading = isPrismClientsLoading && enabled;
  const isError = isPrismClientsError;

  return {
    isLoading,
    isError,
    getPrismLinkAvailability,
  };
};
